import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { instructorsApi } from '../../api/instructors'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import { FiSearch } from 'react-icons/fi'
import { FaGraduationCap } from 'react-icons/fa'
import './Instructors.css'

const Instructors = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['instructors'],
    queryFn: () => instructorsApi.getAll(),
  })

  const instructors = data?.data || []

  // Filter instructors based on search and category
  const filteredInstructors = instructors.filter((instructor) => {
    const matchesSearch = !searchQuery || 
      instructor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.bio?.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = !selectedCategory || 
      instructor.subject?.name === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  // Get unique categories/subjects
  const categories = [...new Set(instructors.map(i => i.subject?.name).filter(Boolean))]

  const handleSearch = (e) => {
    e.preventDefault()
    // Search is handled by filteredInstructors
  }

  return (
    <div className="instructors-page bg-gray-900 min-h-screen font-sans text-white selection:bg-primary-blue selection:text-white">
      <Header />
      <main className="instructors-main">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-blue/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent-orange/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 animate-fade-down">
              نخبة من <span className="text-primary-blue">أفضل الخبراء</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10 animate-fade-up animation-delay-100">
              تعلم على يد نخبة من أفضل المدرسين والخبراء في مجالاتهم. اختر مدرسك وابدأ رحلة التفوق الآن.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative animate-fade-up animation-delay-200">
              <div className="relative group">
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400 group-focus-within:text-primary-blue transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="ابحث عن مدرس أو مادة..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full py-4 pr-12 pl-4 bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all shadow-lg"
                />
              </div>
            </form>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12 relative z-10">
          {/* Category Filters */}
          {categories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12 animate-fade-up animation-delay-300">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  !selectedCategory 
                    ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25 scale-105' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700'
                }`}
              >
                الكل
              </button>
              {categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25 scale-105'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Instructors Grid */}
          {isLoading ? (
            <Loading fullScreen={false} text="جاري التحميل..." />
          ) : error ? (
            <div className="text-center py-20 bg-gray-800/50 rounded-3xl border border-gray-700/50">
              <p className="text-red-400 text-lg">حدث خطأ أثناء تحميل قائمة الخبراء</p>
            </div>
          ) : filteredInstructors.length === 0 ? (
            <div className="text-center py-20 bg-gray-800/50 rounded-3xl border border-gray-700/50">
              <div className="w-20 h-20 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiSearch className="w-10 h-10 text-gray-500" />
              </div>
              <p className="text-gray-400 text-lg">لا توجد نتائج مطابقة لبحثك</p>
              <button 
                onClick={() => {setSearchQuery(''); setSelectedCategory(null)}}
                className="mt-4 text-primary-blue hover:underline"
              >
                مسح فلاتر البحث
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredInstructors.map((instructor, index) => (
                <Link
                  key={instructor.id}
                  to={`/instructors/${instructor.id}`}
                  className="group relative bg-gray-800 rounded-3xl overflow-hidden border border-gray-700 hover:border-primary-blue/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-blue/10 hover:-translate-y-2 animate-fade-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Image Section */}
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent z-10 opacity-60 group-hover:opacity-40 transition-opacity"></div>
                    {instructor.avatar ? (
                      <img
                        src={getStorageUrl(instructor.avatar)}
                        alt={instructor.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                        <span className="text-6xl font-bold text-gray-600 group-hover:text-gray-500 transition-colors">
                          {instructor.name?.[0]?.toUpperCase() || 'I'}
                        </span>
                      </div>
                    )}
                    
                    {/* Subject Badge */}
                    {instructor.subject?.name && (
                      <div className="absolute top-4 right-4 z-20">
                        <span className="px-3 py-1.5 bg-gray-900/80 backdrop-blur-md rounded-xl text-xs font-bold text-white border border-white/10 shadow-lg">
                          {instructor.subject.name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="p-6 relative">
                    {/* Decorative line */}
                    <div className="absolute top-0 right-6 left-6 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary-blue transition-colors">
                        {instructor.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <FaGraduationCap className="text-accent-orange" />
                        <span>{instructor.subject?.name || 'مدرس'}</span>
                      </div>
                    </div>

                    {instructor.bio && (
                      <p className="text-sm text-gray-400 line-clamp-2 mb-4 h-10 leading-relaxed">
                        {instructor.bio}
                      </p>
                    )}

                    {/* Tags */}
                    {instructor.levels && instructor.levels.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {instructor.levels.slice(0, 2).map((level, idx) => (
                          <span key={idx} className="px-2.5 py-1 bg-gray-700/50 rounded-lg text-xs text-gray-300 border border-gray-600/50">
                            {level}
                          </span>
                        ))}
                        {instructor.levels.length > 2 && (
                          <span className="px-2.5 py-1 bg-gray-700/50 rounded-lg text-xs text-gray-300 border border-gray-600/50">
                            +{instructor.levels.length - 2}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/50">
                      <span className="text-sm font-medium text-primary-blue group-hover:translate-x-1 transition-transform duration-300 flex items-center gap-1">
                        عرض الملف الشخصي
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rotate-180" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Instructors
